import { useState } from "react";
import type { ValueOf } from "type-fest";

import { OTP_METHODS, SIGNUP_STEPS } from "~/src/constants/otp";
import type { GetPropertiesQueryResults } from "~/src/helpers/iframeHelpers";
import { generateOTP } from "~/src/helpers/otpHelpers";
import { getRecaptchaToken } from "~/src/helpers/recaptcha";
import { useSimpleTranslate } from "~/src/SimpleTranslateProvider";

import EmailConfirmOTPForm from "../otp-login-email/EmailConfirmOTPForm";
import type { EmailLoginViewProps } from "../otp-login-email/EmailLoginView";
import type { SignupTranslations } from ".";
import EmailSignupForm from "./EmailSignupForm";
// Define specific types for the API responses
interface SignupErrorResponse {
  error: string;
  errors?: Array<{ message: string }>;
}

interface SignupSuccessResponse {
  [key: string]: unknown;
  success?: boolean;
}

interface EmailSignupFormViewProps extends EmailLoginViewProps {
  hideMarketingSection: GetPropertiesQueryResults["hideMarketingSection"];
  isOptinMatrixEnabled: GetPropertiesQueryResults["isOptinMatrixEnabled"];
  marketingSmsOptIn: GetPropertiesQueryResults["marketingSmsOptIn"];
  partnerReservationId: GetPropertiesQueryResults["partnerReservationId"];
  referrer: GetPropertiesQueryResults["referrer"];
  signupFailure: (data: SignupErrorResponse) => void;
  signupStart: () => void;
  signupSuccess: (data: SignupSuccessResponse) => void;
}

/**
 * EmailSignupFormView acts as the coordinator for the signup flow.
 * Currently, it only has one step, but it's structured to support multiple steps in the future.
 */
function EmailSignupFormView({
  hideMarketingSection,
  isOptinMatrixEnabled,
  loginStart,
  marketingSmsOptIn,
  loginSuccess,
  otpTokenLoginStart,
  partnerReservationId,
  referrer,
  signupFailure,
  signupStart,
  signupSuccess,
}: EmailSignupFormViewProps) {
  const translate = useSimpleTranslate<SignupTranslations>();
  const [email, setEmail] = useState("");
  const [otpToken, setOtpToken] = useState("");

  const [signupStep, setSignupStep] = useState<ValueOf<typeof SIGNUP_STEPS>>(
    SIGNUP_STEPS.SIGNUP_FORM,
  );

  const handleClickConfirmOTPFormBack = () => {
    setSignupStep(SIGNUP_STEPS.SIGNUP_FORM);
  };

  if (signupStep === SIGNUP_STEPS.CONFIRM_OTP_FORM) {
    return (
      <EmailConfirmOTPForm
        email={email}
        loginStart={loginStart}
        loginSuccess={loginSuccess}
        onClickBack={handleClickConfirmOTPFormBack}
        onResendOtpTokenSuccess={setOtpToken}
        otpToken={otpToken}
      />
    );
  }

  return (
    <EmailSignupForm
      hideMarketingSection={hideMarketingSection}
      isOptinMatrixEnabled={isOptinMatrixEnabled}
      marketingSmsOptIn={marketingSmsOptIn}
      onEmailAlreadyUsed={async (email) => {
        setEmail(email);
        otpTokenLoginStart();
        const recaptchaTokenOrError = await getRecaptchaToken({ translate });

        const data =
          typeof recaptchaTokenOrError === "string"
            ? await generateOTP({
                body: {
                  email: email,
                  method: OTP_METHODS.EMAIL,
                },
                recaptchaToken: recaptchaTokenOrError,
              })
            : recaptchaTokenOrError;

        if ("error" in data) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          return signupFailure(data as unknown as SignupErrorResponse);
        }
        setOtpToken(data.token);
        setSignupStep(SIGNUP_STEPS.CONFIRM_OTP_FORM);
      }}
      otpTokenLoginStart={otpTokenLoginStart}
      partnerReservationId={partnerReservationId}
      referrer={referrer}
      signupFailure={signupFailure}
      signupStart={signupStart}
      signupSuccess={signupSuccess}
    />
  );
}

export default EmailSignupFormView;
